import type { ReactNode } from "react";
import { cn } from "./cn";

export type ColunaDaTabela<T> = {
  readonly chave: string;
  readonly titulo: string;
  readonly celula: (item: T) => ReactNode;
  /** Número alinha à direita; o resto, à esquerda. */
  readonly numerica?: boolean;
  /** Esconde a coluna em telas estreitas. Use para o que é secundário. */
  readonly ocultarNoCelular?: boolean;
};

export type PropsDaTabela<T> = {
  readonly colunas: readonly ColunaDaTabela<T>[];
  readonly itens: readonly T[];
  readonly chaveDe: (item: T) => string;
  readonly legenda: string;
  /** Marca visualmente a linha correspondente ao item selecionado. */
  readonly chaveSelecionada?: string | null | undefined;
  readonly aoClicarNaLinha?: ((item: T) => void) | undefined;
};

/**
 * Tabela genérica.
 *
 * Decisões que resolvem problema real:
 *
 * - `caption` obrigatória: tabela sem legenda é ilegível em leitor de tela.
 * - overflow horizontal no container, nunca na página: tabela larga no celular não
 *   deve fazer o corpo inteiro rolar de lado.
 * - cabeçalho com `bg-muted/50` e linha com `hover`: sem contraste no cabeçalho, a
 *   tabela vira uma grade de texto e o olho perde a referência ao rolar.
 * - `aria-selected` na linha destacada, para o destaque não ser só cor.
 *
 * Ordenação, seleção múltipla e paginação NÃO estão aqui. Se o seu caso precisa,
 * verifique no inventário (`npm run inv buscar tabela`) antes de criar outra — no
 * CRM esse papel é do `@tanstack/react-table`, que vale a pena quando a tabela
 * passa a ter estado próprio.
 */
export function Tabela<T>({
  colunas,
  itens,
  chaveDe,
  legenda,
  chaveSelecionada,
  aoClicarNaLinha,
}: PropsDaTabela<T>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full caption-bottom border-collapse text-sm">
        <caption className="sr-only">{legenda}</caption>
        <thead>
          <tr className="border-b bg-muted/50 text-left">
            {colunas.map((coluna) => (
              <th
                key={coluna.chave}
                scope="col"
                className={cn(
                  "h-10 px-4 align-middle text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                  coluna.numerica && "text-right",
                  coluna.ocultarNoCelular && "hidden sm:table-cell",
                )}
              >
                {coluna.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {itens.map((item) => {
            const chave = chaveDe(item);
            const selecionada = chaveSelecionada === chave;
            return (
              <tr
                key={chave}
                aria-selected={selecionada}
                onClick={aoClicarNaLinha ? () => aoClicarNaLinha(item) : undefined}
                className={cn(
                  "border-b transition-colors last:border-0",
                  selecionada ? "bg-accent/60" : "hover:bg-muted/40",
                  aoClicarNaLinha && "cursor-pointer",
                )}
              >
                {colunas.map((coluna) => (
                  <td
                    key={coluna.chave}
                    className={cn(
                      "px-4 py-3 align-middle",
                      coluna.numerica && "text-right tabular-nums",
                      coluna.ocultarNoCelular && "hidden sm:table-cell",
                    )}
                  >
                    {coluna.celula(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
